import type { operations } from './generated/schema';

/** `Retry-After` as the contract declares it on polled routes: whole seconds. */
export type RetryAfterSeconds = NonNullable<
  operations['getAnalysisExport']['responses'][200]['headers']['Retry-After']
>;

/**
 * The longest wait a `Retry-After` can ask for: the contract's maximum
 * (`retry_after_headers`, one day). Clamping to it also keeps the wait far inside
 * the browser timer's range (2^31 - 1 ms), past which `setTimeout` fires at once.
 */
export const RETRY_AFTER_MAX_SECONDS = 86_400;

function clampSeconds(seconds: number): RetryAfterSeconds {
  return Math.min(RETRY_AFTER_MAX_SECONDS, Math.max(0, seconds));
}

/**
 * Read a `Retry-After` header in either of its HTTP forms: delta-seconds, or an
 * HTTP date, measured from `now`. The result is whole seconds within
 * `[0, RETRY_AFTER_MAX_SECONDS]`: a date already past means "now", and an
 * oversized value waits the maximum. An absent or unreadable header is no advice.
 */
export function parseRetryAfterSeconds(header: string | null, now: number = Date.now()): RetryAfterSeconds | null {
  if (header === null) return null;
  const value = header.trim();
  if (/^\d+$/.test(value)) return clampSeconds(Number(value));
  // An HTTP date always names its weekday or month; this keeps bare numbers like
  // "-5" or "1.5", which `Date.parse` would read as years, from passing as dates.
  if (!/[a-z]/i.test(value)) return null;
  const instant = Date.parse(value);
  if (Number.isNaN(instant)) return null;
  return clampSeconds(Math.ceil((instant - now) / 1_000));
}
